import { useEffect, useState } from "react";
import {
    Box,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Tooltip
} from "@mui/material";
import axiosClient from "../../api/axiosClient";

// Colori per la percentuale del giocatore (intera riga)
const getRowColor = (percentage: number) => {
    if (percentage < 20) return "#bbdefb";
    if (percentage < 40) return "#90caf9";
    if (percentage < 60) return "#ffe082";
    if (percentage < 80) return "#ffb74d";
    return "#ff8a65";
};

interface MatchInfo {
    id: number;
    label: string;
    date: string;
}

interface PlayerUsage {
    playerId: number;
    playerName: string;
    setsPerMatch: Record<number, number>;
    total: number;
    percentage: number;
    trainingSetsPresent: number;
}

interface UsageTableResponse {
    matches: MatchInfo[];
    players: PlayerUsage[];
    maxSetInAnyMatch: number;
    trainingTotalSets: number;
}

export default function TrainingUsageTable({ tournamentId }: { tournamentId: number | null }) {
    const [data, setData] = useState<UsageTableResponse | null>(null);
    const [loading, setLoading] = useState(true);

    const [sortBy, setSortBy] =
        useState<"name" | "total" | "percentage">("percentage");
    const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

    useEffect(() => {
        axiosClient
            .get("/usage/getTrainingTable", { params: { tournamentId } })
            .then(res => setData(res.data))
            .finally(() => setLoading(false));
    }, [tournamentId]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" mt={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (!data) return <Typography>Errore nel caricamento.</Typography>;

    // 👉 Ordinamento
    const sortedPlayers = [...data.players].sort((a, b) => {
        let valueA: any;
        let valueB: any;

        switch (sortBy) {
            case "name":
                valueA = a.playerName.toLowerCase();
                valueB = b.playerName.toLowerCase();
                break;
            case "total":
                valueA = a.total;
                valueB = b.total;
                break;
            case "percentage":
                valueA = a.percentage;
                valueB = b.percentage;
                break;
        }

        if (valueA < valueB) return sortDir === "asc" ? -1 : 1;
        if (valueA > valueB) return sortDir === "asc" ? 1 : -1;
        return 0;
    });

    const toggleSort = (
        field: "name" | "total" | "percentage"
    ) => {
        if (sortBy === field) {
            setSortDir(sortDir === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortDir("asc");
        }
    };

    const headerStyle = { cursor: "pointer", userSelect: "none" };

    return (
        <TableContainer component={Paper} sx={{ mt: 1 }}>
            <Table
                size="small"
                sx={{
                    "& th": { fontSize: 13, padding: "2px 4px", whiteSpace: "nowrap" },
                    "& td": { fontSize: 12, padding: "2px 4px", whiteSpace: "nowrap" }
                }}
            >
                <TableHead>
                    <TableRow>
                        <TableCell onClick={() => toggleSort("name")} sx={headerStyle}>
                            <strong>
                                Giocatore {sortBy === "name" ? (sortDir === "asc" ? "▲" : "▼") : ""}
                            </strong>
                        </TableCell>

                        {data.matches.map((m, index) => (
                            <TableCell key={m.id} align="center"
                                sx={{
                                    width: 28,
                                    maxWidth: 28,
                                    padding: "2px",
                                    fontSize: 11
                                }}
                            >
                                <Tooltip title={m.label}>
                                    <strong>T{index + 1}</strong>
                                </Tooltip>
                            </TableCell>
                        ))}

                        <TableCell
                            align="center"
                            onClick={() => toggleSort("total")}
                            sx={headerStyle}
                        >
                            <strong>
                                Tot {sortBy === "total" ? (sortDir === "asc" ? "▲" : "▼") : ""}
                            </strong>
                        </TableCell>

                        <TableCell
                            align="center"
                            onClick={() => toggleSort("percentage")}
                            sx={headerStyle}
                        >
                            <strong>
                                % {sortBy === "percentage" ? (sortDir === "asc" ? "▲" : "▼") : ""}
                            </strong>
                        </TableCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    {sortedPlayers.map(p => {
                        return (
                            <TableRow
                                key={p.playerId}
                                sx={{ backgroundColor: getRowColor(p.percentage) }}
                            >
                                <TableCell>
                                    <strong>{p.playerName}</strong>
                                </TableCell>

                                {data.matches.map(m => {
                                    const value = p.setsPerMatch[m.id] ?? 0;
                                    return (
                                        <TableCell key={m.id} align="center"
                                            sx={{
                                                width: 28,
                                                maxWidth: 28,
                                                padding: "1px",
                                                fontSize: 12
                                            }}
                                        >
                                            <Tooltip title={m.label}>
                                                 <strong>{value}</strong>
                                            </Tooltip>
                                           
                                        </TableCell>
                                    );
                                })}

                                <TableCell align="center">
                                    <strong>{p.total}</strong>
                                </TableCell>

                                <TableCell align="center">
                                    {p.percentage.toFixed(1)}%
                                </TableCell>

                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
